import type { ReactNode } from "react"
import { useMemo, useState } from "react"
import { IconPaperPlane4Fill18 as PaperPlane } from "nucleo-ui-fill-18"
import {
	IconAtom2Outline18 as Atom,
	IconBellOutline18 as Bell,
	IconChevronLeftOutline18 as ChevronLeft,
	IconArrowDownToLineOutline18 as Download,
	IconShareUpRightOutline18 as ExternalLink,
	IconEyeOutline18 as Eye,
	IconGlobeOutline18 as Globe,
	IconHashtagOutline18 as Hashtag,
	IconLayersOutline18 as Layers,
	IconLink3Outline18 as Link,
	IconMagnifierOutline18 as Magnifier,
	IconEnvelopeOutline18 as Mail,
	IconPencilOutline18 as Pencil,
	IconPlusOutline18 as Plus,
	IconSquareArrowUpRightOutline18 as SquareArrowOutUpRight,
	IconTagOutline18 as Tag,
	IconTrash2Outline18 as Trash2,
	IconUsersOutline18 as Users,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Badge,
	Button,
	Checkbox,
	IconButton,
	IconWithHalo,
	Input,
	Popover,
	ProgressBar,
	SegmentedControl,
	Select,
	SelectTrigger,
	Switch,
	Textarea,
	Tooltip,
} from "@productlane/ui"
import { SortIcon } from "@productlane/ui/src/icons/app-nav-icons"
import { AiAssistantIcon } from "@productlane/ui/src/icons/project/ai-assistant-icon"
import { CalendarIcon } from "@productlane/ui/src/icons/project/calendar-icon"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { SlackIcon } from "@productlane/ui/src/icons/slack-logo"

import { CollapseArrowIcon } from "@/components/shared/collapse-arrow-icon"
import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"
import { BreadcrumbSlash } from "../../app/page-header"
import { SettingsHeader } from "../../settings/settings-header"
import {
	settingsNavGroupClass,
	SettingsNavRow,
} from "../../settings/settings-pages/settings-nav-row"
import { SettingItem } from "../../settings/settings-switches/setting-item"
import {
	SETTINGS_CARD_CLASS,
	SettingsCardInsetContext,
	SettingsContainer,
} from "../../settings/settings-switches/settings-container"
import { DesignIssueHeader } from "./design-issue-header"

const POST = {
	title: "Agent API is now public",
	slug: "agent-api",
	date: "2026-10-08",
	dateLabel: "Oct 8, 2026",
	body: "Start the Linear agent from any tool. One call files the issue, picks the team and links the customer.",
}

const PORTAL_DISPLAY_URL = "feedback.linear.app/changelog"
const SUBSCRIBERS = 2140

const LINK_OPTIONS = [
	{ value: "productlane", label: "Productlane changelog" },
	{ value: "custom", label: "Your website" },
	{ value: "none", label: "No link" },
]

const TAGS = [
	{ id: "feature", name: "Feature", color: "#5e6ad2" },
	{ id: "improvement", name: "Improvement", color: "#26b5ce" },
	{ id: "fix", name: "Fix", color: "#f2994a" },
]

const PREFIXES = ["ent", "acme", "cs", "beta", "partner", "team"]
const COMPANIES = [
	"openai",
	"figma",
	"vercel",
	"ramp",
	"notion",
	"stripe",
	"retool",
	"raycast",
	"supabase",
	"loom",
	"mercury",
	"brex",
	"plaid",
	"cursor",
	"replit",
	"posthog",
	"sentry",
	"zapier",
	"airtable",
	"miro",
	"canva",
	"webflow",
	"framer",
	"pitch",
	"attio",
	"clay",
	"hex",
	"modal",
	"runway",
	"perplexity",
	"anthropic",
	"mistral",
	"deel",
	"rippling",
	"gusto",
	"linear-labs",
	"arc",
	"warp",
	"zed",
	"tldraw",
	"descript",
	"superhuman",
	"height",
	"coda",
	"monday",
	"asana",
	"clickup",
	"jira",
	"gitlab",
	"github",
	"datadog",
	"grafana",
	"netlify",
	"render",
	"fly",
	"railway",
	"neon",
	"planetscale",
	"turso",
	"convex",
	"resend",
	"postmark",
	"loops",
	"intercom",
	"zendesk",
	"hubspot",
	"segment",
	"amplitude",
	"mixpanel",
	"heap",
	"pendo",
	"calendly",
	"cal",
	"gong",
	"chorus",
	"outreach",
	"apollo",
	"lemlist",
	"typefully",
	"buffer",
	"hootsuite",
	"sprout",
	"later",
	"dovetail",
	"maze",
	"useberry",
	"lookback",
	"ashby",
	"greenhouse",
	"lever",
	"workable",
	"remote",
	"oyster",
	"papaya",
	"pilot",
	"mosaic",
	"causal",
	"runway-fin",
	"vanta",
	"drata",
	"secureframe",
	"1password",
	"dashlane",
	"tailscale",
	"cloudflare",
	"fastly",
	"akamai",
	"algolia",
	"meili",
	"typesense",
	"pinecone",
	"weaviate",
	"qdrant",
	"chroma",
	"langchain",
	"llamaindex",
	"humanloop",
	"langfuse",
	"braintrust",
	"wandb",
	"scale",
	"snorkel",
	"labelbox",
	"elevenlabs",
	"deepgram",
	"assembly",
	"speechmatics",
]

const CHANNELS = PREFIXES.flatMap((prefix) =>
	COMPANIES.map((company) => ({
		id: `${prefix}-${company}`,
		name: `${prefix}-${company}`,
		shared: prefix !== "team",
	})),
)

const FAILED = [
	{ name: "acme-arc", reason: "Channel is archived" },
	{ name: "beta-ramp", reason: "Bot is no longer in this channel" },
	{ name: "ent-globex", reason: "Only admins can post here" },
	{ name: "partner-heap", reason: "Channel no longer exists" },
	{ name: "cs-pitch", reason: "Bot is no longer in this channel" },
	{ name: "ent-later", reason: "Channel is archived" },
]

function resolveLink(mode: string, pattern: string) {
	if (mode === "none") return null
	if (mode === "productlane")
		return `https://feedback.linear.app/changelog/${POST.slug}`
	return pattern.replaceAll("{slug}", POST.slug).replaceAll("{date}", POST.date)
}

function NewBadge() {
	return (
		<Badge variant="green" size="sm">
			New
		</Badge>
	)
}

function ChangedBadge() {
	return (
		<Badge variant="amber" size="sm">
			Changed
		</Badge>
	)
}

function Marked({
	children,
	badge,
}: {
	children: ReactNode
	badge: ReactNode
}) {
	return (
		<span className="flex items-center gap-2">
			{children}
			{badge}
		</span>
	)
}

function SettingIconBox({ children }: { children: ReactNode }) {
	return (
		<span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gray-100 text-primary dark:bg-gray-800 [&_svg]:h-[18px] [&_svg]:w-[18px]">
			{children}
		</span>
	)
}

// Same markup as PublishPortalCard, which reads the live workspace.
function PublishCard({
	checked,
	onCheckedChange,
}: {
	checked: boolean
	onCheckedChange: (value: boolean) => void
}) {
	return (
		<div className={cn(SETTINGS_CARD_CLASS, "p-4 sm:-mx-3.5")}>
			<div className="flex w-full flex-col items-start gap-2 text-sm">
				<div className="flex w-full select-none items-center justify-between">
					<div className="flex w-full flex-col items-start gap-0.5">
						<div className="flex w-full flex-row items-center justify-between gap-4">
							<div className="text-sm font-medium">
								{checked ? "Changelog is public" : "Make Changelog public"}
							</div>
							<Switch
								label={null}
								name="public"
								checked={checked}
								onCheckedChange={onCheckedChange}
							/>
						</div>
						<div className="text-xs font-medium text-secondary">
							Customers can access the changelog with this link.
						</div>
					</div>
				</div>
				<Input
					readOnly
					value={PORTAL_DISPLAY_URL}
					name="domain"
					className="mt-3 w-full flex-1"
					inputClassName="hover:text-secondary"
					icon={SquareArrowOutUpRight}
					iconPosition="right"
				/>
			</div>
		</div>
	)
}

function LinkSettingCard({
	mode,
	pattern,
	onMode,
	onPattern,
	dimmed,
}: {
	mode: string
	pattern: string
	onMode: (mode: string) => void
	onPattern: (pattern: string) => void
	dimmed: boolean
}) {
	const example = resolveLink(mode, pattern)
	const unknownPlaceholder = /\{(?!slug\}|date\})[^}]*\}/.test(pattern)
	return (
		<SettingsContainer
			className={cn("mb-4 transition-opacity duration-200 ease-in-out", {
				"opacity-50": dimmed,
			})}
		>
			<SettingItem
				Icon={Link}
				title={<Marked badge={<NewBadge />}>Changelog link</Marked>}
				description="Where the Read more button points."
				action={
					<Select
						name="link-mode"
						value={mode}
						options={LINK_OPTIONS}
						onChange={onMode}
						className="w-52"
						selectClassName="dark:bg-gray-700/70"
					/>
				}
			/>
			{mode === "custom" && (
				<div className="space-y-1.5 pt-4">
					<Input
						name="link-pattern"
						value={pattern}
						onChange={(e) => onPattern(e.target.value)}
						errorMessage={
							unknownPlaceholder
								? "Use {slug} or {date} as placeholders."
								: undefined
						}
						showErrorMessage={unknownPlaceholder}
					/>
					<div className="text-xs text-tertiary">
						Example: <span className="text-secondary">{example}</span>
					</div>
				</div>
			)}
		</SettingsContainer>
	)
}

function resolveUrl(url: string) {
	if (!url.trim()) return `https://${PORTAL_DISPLAY_URL}/${POST.slug}`
	return url.replaceAll("{slug}", POST.slug).replaceAll("{date}", POST.date)
}

function LinkUrlCard({
	url,
	onUrl,
	dimmed,
}: {
	url: string
	onUrl: (url: string) => void
	dimmed: boolean
}) {
	return (
		<SettingsContainer
			className={cn("mb-4 transition-opacity duration-200 ease-in-out", {
				"opacity-50": dimmed,
			})}
		>
			<SettingItem
				Icon={Link}
				title={<Marked badge={<NewBadge />}>Changelog link</Marked>}
				description='Choose where "Read more" goes in changelog emails and Slack posts.'
			/>
			<div className="space-y-1.5 pt-4">
				<Input
					name="link-url"
					value={url}
					placeholder={`${PORTAL_DISPLAY_URL}/{slug}`}
					onChange={(e) => onUrl(e.target.value)}
				/>
				<div className="text-xs text-tertiary">
					{url.trim() ? (
						<>
							Example: <span className="text-secondary">{resolveUrl(url)}</span>
						</>
					) : (
						"Leave empty for your Productlane changelog. Use {slug} and {date} per post."
					)}
				</div>
			</div>
		</SettingsContainer>
	)
}

function LinkNavRow({ link, dimmed }: { link: string; dimmed: boolean }) {
	return (
		<SettingsContainer
			className={cn("mb-4 transition-opacity duration-200 ease-in-out", {
				"opacity-50": dimmed,
			})}
		>
			<SettingItem
				Icon={Link}
				title={<Marked badge={<NewBadge />}>Slack and email posts</Marked>}
				description={<span className="truncate">Links to {link}</span>}
				link="/design/changelog-slack-broadcast"
			/>
		</SettingsContainer>
	)
}

function LinkSubpage({
	mode,
	url,
	onMode,
	onUrl,
}: {
	mode: "productlane" | "custom"
	url: string
	onMode: (mode: "productlane" | "custom") => void
	onUrl: (url: string) => void
}) {
	const link =
		mode === "productlane"
			? `https://${PORTAL_DISPLAY_URL}/${POST.slug}`
			: resolveUrl(url)
	return (
		<>
			<div className="-mt-6 mb-6">
				<Button variant="ghost" Icon={ChevronLeft}>
					Changelog
				</Button>
			</div>
			<SettingsHeader
				title="Slack and email posts"
				subtitle="How each changelog looks when it goes out."
			/>
			<SettingsContainer className="mb-4">
				<SettingItem
					Icon={Link}
					title={<Marked badge={<NewBadge />}>Read more button</Marked>}
					description="Where customers go from the post."
					action={
						<SegmentedControl
							name="link-target"
							value={mode}
							onValueChange={onMode}
							showLabels
							className="[&>*]:px-1"
							options={[
								{ value: "productlane", label: "Productlane", Icon: Globe },
								{ value: "custom", label: "Your website", Icon: Link },
							]}
						/>
					}
				/>
				{mode === "custom" && (
					<div className="space-y-1.5 pt-4">
						<Input
							name="link-url-c"
							value={url}
							placeholder="https://yoursite.com/changelog/{slug}"
							onChange={(e) => onUrl(e.target.value)}
						/>
						<div className="text-xs text-tertiary">
							Use {"{slug}"} and {"{date}"} to fill in each post.
						</div>
					</div>
				)}
			</SettingsContainer>
			<div className="space-y-2">
				<div className="flex items-center gap-2 text-sm font-medium">
					Preview <NewBadge />
				</div>
				<div className="rounded-lg bg-white py-2 ring-1 ring-black/5 dark:ring-white/10">
					<SlackPost link={link} variant="button" compact />
				</div>
				<div className="text-xs text-tertiary">
					Opens <span className="text-secondary">{link}</span>
				</div>
			</div>
		</>
	)
}

function ChangelogSettingsReplica({
	renderLink,
}: {
	renderLink: (dimmed: boolean) => ReactNode
}) {
	const [isPublic, setIsPublic] = useState(true)
	const [emailNotifications, setEmailNotifications] = useState(true)
	const [subscribeEnabled, setSubscribeEnabled] = useState(false)
	const [subscribeUrl, setSubscribeUrl] = useState("")
	const [drafts, setDrafts] = useState(true)
	const [prompt, setPrompt] = useState("")
	const dim = { "opacity-50": !isPublic }

	return (
		<>
			<SettingsHeader
				title="Changelog"
				subtitle="Publish a beautiful changelog and notify users in-app."
				docsArticle="90840b98-ee11-4eb0-b7a0-c24c8196c646"
			/>

			<PublishCard checked={isPublic} onCheckedChange={setIsPublic} />

			<div
				className={cn(
					settingsNavGroupClass,
					"my-4 transition-opacity duration-200 ease-in-out",
					dim,
				)}
			>
				<SettingsNavRow
					icon={SortIcon}
					title="Portal settings"
					description="Set up SSO, custom domain, theme and a private portal."
					link="/settings/portal"
				/>
				<SettingsNavRow
					icon={Users}
					title="Multiple portals"
					description="Activate the support portal for individual customer portals."
					link="/settings/support-portal"
				/>
			</div>

			<SettingsContainer
				className={cn("mb-4 transition-opacity duration-200 ease-in-out", dim)}
			>
				<div className="flex items-center justify-between">
					<div className="flex flex-row items-center gap-4">
						<SettingIconBox>
							<Tag />
						</SettingIconBox>
						<div className="flex flex-1 flex-col gap-1">
							<div className="text-sm font-medium">Changelog labels</div>
							<div className="text-xs font-medium text-secondary">
								Label your changelog posts and let visitors filter by it.
							</div>
						</div>
					</div>
					<Button variant="outline" size="sm" Icon={Plus}>
						New label
					</Button>
				</div>
				<div className="mt-4 flex flex-col gap-1 pt-2">
					{TAGS.map((tag) => (
						<div
							key={tag.id}
							className="group flex cursor-default items-center justify-between rounded-md px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800/50"
						>
							<div className="flex items-center gap-2">
								<span
									className="inline-block h-2.5 w-2.5 rounded-full"
									style={{ backgroundColor: tag.color }}
								/>
								<span className="text-sm">{tag.name}</span>
							</div>
							<div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
								<Tooltip label="Edit label">
									<IconButton variant="ghost" Icon={Pencil} size="sm" />
								</Tooltip>
								<Tooltip label="Delete label">
									<IconButton
										variant="ghost"
										Icon={Trash2}
										size="sm"
										className="text-red-500 hover:text-red-600"
									/>
								</Tooltip>
							</div>
						</div>
					))}
				</div>
			</SettingsContainer>

			<SettingsContainer
				className={cn("mb-4 transition-opacity duration-200 ease-in-out", dim)}
			>
				<SettingItem
					Icon={Bell}
					title="Email notifications"
					description="Let visitors subscribe and email them when you publish a post."
					action={
						<Switch
							name="changelogEmailNotifications"
							checked={emailNotifications}
							onCheckedChange={setEmailNotifications}
						/>
					}
				/>
				<div className="pt-3">
					<div className="flex flex-col gap-2">
						<div className="flex items-center justify-between pr-1">
							<div className="flex flex-row items-center gap-4">
								<SettingIconBox>
									<Mail />
								</SettingIconBox>
								<div className="flex flex-1 flex-col gap-1">
									<div className="text-sm font-medium">Email subscription</div>
									<div className="text-xs font-medium text-secondary">
										Add a custom email subscription URL for your changelog.
									</div>
								</div>
							</div>
							<Switch
								name="subscribeUrl"
								label={null}
								checked={subscribeEnabled}
								onCheckedChange={setSubscribeEnabled}
							/>
						</div>
						{subscribeEnabled && (
							<div className="mt-4 flex items-end gap-2 pr-1">
								<div className="h-4 w-6 flex-none" />
								<div className="min-w-0 flex-1">
									<Input
										placeholder="example.com/signup"
										value={subscribeUrl}
										onChange={(e) => setSubscribeUrl(e.target.value)}
										name="subscribe-url"
										className="mb-1"
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</SettingsContainer>

			{renderLink(!isPublic)}

			<SettingsContainer
				className={cn("transition-opacity duration-200 ease-in-out", dim)}
			>
				<SettingItem
					Icon={Atom}
					title="Release intelligence"
					description="Drafts a Changelog from completed Linear projects, closed issues, and GitHub commits."
					action={
						<Switch
							name="drafts"
							checked={drafts}
							onCheckedChange={setDrafts}
						/>
					}
				/>
				{drafts && (
					<div className="pt-2">
						<div className="flex flex-col gap-2">
							<div className="text-sm font-medium">Custom prompt</div>
							<div className="text-xs font-medium text-secondary">
								Add custom instructions for AI-generated changelog entries.
							</div>
							<Textarea
								name="changelogCustomPrompt"
								placeholder="e.g., Focus on user-facing changes, use a friendly tone..."
								value={prompt}
								onChange={(e) => setPrompt(e.target.value)}
								minRows={3}
							/>
						</div>
					</div>
				)}
			</SettingsContainer>
		</>
	)
}

type Scope = "all" | "chosen"

function useChannelSelection() {
	const [scope, setScope] = useState<Scope>("chosen")
	const [selected, setSelected] = useState(
		() =>
			new Set(
				CHANNELS.filter((c) => c.name.startsWith("ent-")).map((c) => c.id),
			),
	)
	const count = scope === "all" ? CHANNELS.length : selected.size
	return { scope, setScope, selected, setSelected, count }
}

type Selection = ReturnType<typeof useChannelSelection>

function ChannelList({
	selection,
	listClassName,
}: {
	selection: Selection
	listClassName?: string
}) {
	const { scope, setScope, selected, setSelected } = selection
	const [query, setQuery] = useState("")
	const matches = useMemo(
		() => CHANNELS.filter((c) => c.name.includes(query.trim().toLowerCase())),
		[query],
	)
	const toggle = (id: string, checked: boolean) => {
		const next = new Set(selected)
		if (checked) next.add(id)
		else next.delete(id)
		setSelected(next)
	}

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="space-y-2 border-b border-gray-200 p-2.5 dark:border-gray-700/80">
				<SegmentedControl
					name="channel-scope"
					value={scope}
					onValueChange={setScope}
					showLabels
					className="flex w-full [&>*]:flex-1"
					options={[
						{ value: "all", label: "All channels", Icon: Layers },
						{ value: "chosen", label: "Choose channels", Icon: Hashtag },
					]}
				/>
				{scope === "chosen" && (
					<Input
						name="channel-search"
						placeholder="Search channels"
						icon={Magnifier}
						value={query}
						onChange={(e) => setQuery(e.target.value)}
					/>
				)}
			</div>
			{scope === "all" ? (
				<div className="flex-1 p-3 text-sm text-secondary">
					Posts to every channel the bot is in, including channels it joins
					later.
				</div>
			) : (
				<>
					<div className="flex items-center justify-between px-3 pt-2 text-xs">
						<button
							type="button"
							className="font-medium text-blue-600 hover:underline dark:text-blue-400"
							onClick={() =>
								setSelected(new Set([...selected, ...matches.map((m) => m.id)]))
							}
						>
							Select all {matches.length} {query ? "matches" : "channels"}
						</button>
						<button
							type="button"
							className="text-tertiary hover:text-primary"
							onClick={() => {
								const next = new Set(selected)
								matches.forEach((m) => next.delete(m.id))
								setSelected(next)
							}}
						>
							Clear
						</button>
					</div>
					<div
						className={cn(
							"min-h-0 flex-1 space-y-0.5 overflow-y-auto p-1.5 pb-2",
							listClassName,
						)}
					>
						{matches.map((c) => (
							<div
								key={c.id}
								className="flex items-center justify-between gap-2 rounded-md px-1.5 py-1 hover:bg-gray-100 dark:hover:bg-gray-800"
							>
								<Checkbox
									name={c.id}
									size="sm"
									label={`#${c.name}`}
									checked={selected.has(c.id)}
									onCheckedChange={(v) => toggle(c.id, v === true)}
								/>
								{c.shared && (
									<Badge size="sm" variant="light-gray">
										Shared
									</Badge>
								)}
							</div>
						))}
						{matches.length === 0 && (
							<div className="px-1.5 py-2 text-sm text-tertiary">
								No channel matches &quot;{query}&quot;.
							</div>
						)}
					</div>
				</>
			)}
			<div className="border-t border-gray-200 px-3 py-2 text-xs text-tertiary dark:border-gray-700/80">
				{scope === "all"
					? `${CHANNELS.length} channels`
					: `${selected.size} of ${CHANNELS.length} selected`}
			</div>
		</div>
	)
}

function channelLabel(selection: Selection) {
	return selection.scope === "all"
		? `All channels (${CHANNELS.length})`
		: `${selection.selected.size} channels`
}

function ChannelPicker({ selection }: { selection: Selection }) {
	return (
		<Popover
			align="start"
			className="w-80 p-0"
			content={<ChannelList selection={selection} listClassName="max-h-64" />}
		>
			<SelectTrigger className="h-7 w-auto min-w-36 dark:bg-gray-700/70">
				{channelLabel(selection)}
			</SelectTrigger>
		</Popover>
	)
}

type PostVariant = "button" | "title-link" | "two-buttons"

function SlackPost({
	link,
	variant,
	compact,
}: {
	link: string | null
	variant: PostVariant
	compact?: boolean
}) {
	const titleIsLink = variant === "title-link" && link
	return (
		<div className="flex gap-2 px-5 py-2 font-[Lato,ui-sans-serif,system-ui]">
			<div className="mt-0.5 flex size-9 flex-none items-center justify-center rounded-md bg-[#5e6ad2] text-sm font-bold text-white">
				L
			</div>
			<div className="min-w-0 flex-1 space-y-1.5">
				<div className="flex items-baseline gap-1.5">
					<span className="text-[15px] font-bold text-[#1d1c1d]">Linear</span>
					<span className="rounded-sm bg-[#1d1c1d]/[0.08] px-1 text-[10px] font-semibold text-[#616061]">
						APP
					</span>
					<span className="text-xs text-[#616061]">10:42 AM</span>
				</div>
				<div
					className={`flex items-end rounded-lg bg-gradient-to-br from-[#16171d] to-[#2b2d3a] p-5 ${compact ? "h-28" : "h-44"} max-w-md`}
				>
					<span className="text-lg font-semibold text-white">Agent API</span>
				</div>
				<div
					className={cn(
						"text-[18px] font-black leading-tight",
						titleIsLink ? "text-[#1264a3] underline" : "text-[#1d1c1d]",
					)}
				>
					{POST.title}
				</div>
				<div className="text-[13px] text-[#616061]">{POST.dateLabel}</div>
				<div className="max-w-md text-[15px] leading-[22px] text-[#1d1c1d]">
					{POST.body}
				</div>
				{link && variant !== "title-link" && (
					<div className="flex items-center gap-2 pt-1">
						<span className="inline-flex h-7 items-center rounded-md border border-[#1d1c1d]/30 bg-white px-3 text-[13px] font-bold text-[#1d1c1d]">
							Read more
						</span>
						{variant === "two-buttons" && (
							<span className="inline-flex h-7 items-center rounded-md border border-[#1d1c1d]/30 bg-white px-3 text-[13px] font-bold text-[#1d1c1d]">
								All updates
							</span>
						)}
					</div>
				)}
				{link && !compact && (
					<div className="text-[11px] text-[#616061]">{link}</div>
				)}
			</div>
		</div>
	)
}

function DialogShell({
	title,
	description,
	width = "max-w-[38rem]",
	children,
	footer,
}: {
	title: string
	description: string
	width?: string
	children: ReactNode
	footer: ReactNode
}) {
	return (
		<div
			className={cn(
				"relative flex w-full flex-col overflow-hidden rounded-3xl bg-[#FCFCFC] p-6 shadow-lg outline-none ring-1 ring-gray-900/20 dark:bg-gray-900 dark:shadow-black dark:ring-white/10",
				width,
			)}
		>
			<div className="mb-1.5 flex flex-col space-y-2 text-left">
				<div className="text-base font-semibold text-gray-900 dark:text-gray-50">
					{title}
				</div>
				<div className="text-sm text-secondary">
					<div className="space-y-3">
						<div>{description}</div>
						{children}
					</div>
				</div>
			</div>
			{footer}
			<div className="absolute right-4 top-4">
				<IconButton variant="ghost" Icon={XIcon} />
			</div>
		</div>
	)
}

function InAppRow() {
	return (
		<div className="flex items-center justify-between gap-2">
			<div className="flex flex-1 items-center gap-2">
				<Tooltip label="Always on. Turn changelog notifications off in Widget settings.">
					<span className="flex">
						<Switch
							name="notify-in-app"
							checked={true}
							disabled
							switchClassName="pointer-events-none"
							variant="branded"
							size="small"
						/>
					</span>
				</Tooltip>
				<span className="text-sm text-secondary">
					Notify contacts via the in-app widget
				</span>
			</div>
			<Button variant="outline" size="sm">
				Widget settings
			</Button>
		</div>
	)
}

function EmailRow({
	email,
	onEmail,
}: {
	email: boolean
	onEmail: (v: boolean) => void
}) {
	return (
		<div className="flex items-center justify-between gap-2">
			<div className="flex flex-1 items-center gap-2">
				<Switch
					name="send-email"
					checked={email}
					onCheckedChange={onEmail}
					variant="branded"
					size="small"
				/>
				<span className="text-sm text-secondary">
					Notify{" "}
					<span className="font-medium text-primary">
						{SUBSCRIBERS.toLocaleString("en-US")}
					</span>{" "}
					subscribers via email
				</span>
				<Tooltip label="Download emails as CSV">
					<IconButton
						Icon={Download}
						variant="ghost"
						iconClassName="text-tertiary group-hover:text-primary"
						className="group"
					/>
				</Tooltip>
				<Tooltip label="View all recipients">
					<IconButton
						Icon={Users}
						variant="ghost"
						iconClassName="text-tertiary group-hover:text-primary"
						className="group"
					/>
				</Tooltip>
			</div>
			<Tooltip label="Send test email to yourself">
				<Button variant="outline" size="sm">
					Send test email
				</Button>
			</Tooltip>
		</div>
	)
}

function DetailsToggle() {
	const [open, setOpen] = useState(false)
	return (
		<div className="-mx-1 px-[5px] py-1.5">
			<Button
				variant="outline"
				size="sm"
				className="gap-1"
				onClick={() => setOpen((v) => !v)}
			>
				<span>Edit subject, sender & from</span>
				<span
					className={cn(
						"flex items-center justify-center text-secondary",
						open && "rotate-90",
					)}
				>
					<CollapseArrowIcon className="h-4 w-4" />
				</span>
			</Button>
		</div>
	)
}

function DialogFooter({ label }: { label: string }) {
	return (
		<div className="mt-2 flex w-full items-center justify-end gap-2">
			<Button variant="default" Icon={PaperPlane}>
				{label}
			</Button>
		</div>
	)
}

const ROWS_CLASS =
	"my-2 flex select-none flex-col gap-3 border-y border-gray-200 py-5 dark:border-gray-700"

function LinkOverride({
	defaultLink,
	className,
}: {
	defaultLink: string | null
	className?: string
}) {
	const [override, setOverride] = useState<string | null>(null)
	const link = override ?? defaultLink ?? ""
	return (
		<div className={cn("flex items-start gap-2", className)}>
			<span className="pt-1.5 text-sm text-tertiary">Link</span>
			<div className="flex-1 space-y-1">
				<Input
					name="send-link"
					value={link}
					placeholder="No link in this post"
					onChange={(e) => setOverride(e.target.value)}
				/>
				<div className="text-xs text-tertiary">
					{override === null
						? "From your changelog settings."
						: "For this post only."}
				</div>
			</div>
			<span className="pt-1.5">
				<NewBadge />
			</span>
		</div>
	)
}

// Version A: static copy of SendUpdateModal ("Publish changelog") with the picker as a popover.
function PublishDialogA({ defaultLink }: { defaultLink: string | null }) {
	const [email, setEmail] = useState(true)
	const [slack, setSlack] = useState(true)
	const [showPreview, setShowPreview] = useState(true)
	const selection = useChannelSelection()

	return (
		<DialogShell
			title="Publish changelog"
			description="Notify your contacts about this update."
			footer={
				<>
					<DetailsToggle />
					<DialogFooter
						label={email || slack ? "Publish and notify" : "Publish"}
					/>
				</>
			}
		>
			<div className={ROWS_CLASS}>
				<InAppRow />
				<EmailRow email={email} onEmail={setEmail} />
				<div className="flex items-center justify-between gap-2">
					<div className="flex flex-1 items-center gap-2">
						<Switch
							name="send-slack"
							checked={slack}
							onCheckedChange={setSlack}
							variant="branded"
							size="small"
						/>
						<span className="text-sm text-secondary">Notify</span>
						<ChannelPicker selection={selection} />
						<span className="text-sm text-secondary">in Slack</span>
						<ChangedBadge />
					</div>
					<Tooltip label="Send test Slack post to your notification channel">
						<Button variant="outline" size="sm">
							Send test Slack
						</Button>
					</Tooltip>
				</div>
				{slack && <LinkOverride defaultLink={defaultLink} className="pl-11" />}
				{slack && (
					<div className="flex items-center gap-2 pl-11">
						<Button
							variant="ghost"
							size="sm"
							Icon={Eye}
							onClick={() => setShowPreview((v) => !v)}
						>
							{showPreview ? "Hide preview" : "Preview"}
						</Button>
						<NewBadge />
					</div>
				)}
				{slack && showPreview && (
					<div className="rounded-lg bg-white py-2 ring-1 ring-black/5 dark:ring-white/10">
						<SlackPost link={defaultLink} variant="button" compact />
					</div>
				)}
			</div>
		</DialogShell>
	)
}

// Version B: the Slack row opens a full-height side sheet with the channel list and the preview.
function PublishDialogB({ defaultLink }: { defaultLink: string | null }) {
	const [email, setEmail] = useState(true)
	const [slack, setSlack] = useState(true)
	const selection = useChannelSelection()

	return (
		<div className="space-y-6">
			<DialogShell
				title="Publish changelog"
				description="Notify your contacts about this update."
				footer={
					<>
						<DetailsToggle />
						<DialogFooter
							label={email || slack ? "Publish and notify" : "Publish"}
						/>
					</>
				}
			>
				<div className={ROWS_CLASS}>
					<InAppRow />
					<EmailRow email={email} onEmail={setEmail} />
					<div className="flex items-center justify-between gap-2">
						<div className="flex flex-1 items-center gap-2">
							<Switch
								name="send-slack-b"
								checked={slack}
								onCheckedChange={setSlack}
								variant="branded"
								size="small"
							/>
							<span className="text-sm text-secondary">
								Notify{" "}
								<span className="font-medium text-primary">
									{selection.count}
								</span>{" "}
								Slack channels
							</span>
							<ChangedBadge />
						</div>
						<Button variant="outline" size="sm" Icon={Hashtag}>
							Choose channels
						</Button>
					</div>
				</div>
			</DialogShell>
			<div className="text-xs text-tertiary">
				Choose channels opens this sheet on the right edge of the screen:
			</div>
			<div className="ml-auto flex h-[720px] w-[420px] flex-col overflow-hidden rounded-l-2xl bg-[#FCFCFC] shadow-lg ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
				<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700/80">
					<div className="flex items-center gap-2 text-sm font-semibold text-primary">
						Slack channels <NewBadge />
					</div>
					<IconButton variant="ghost" Icon={XIcon} />
				</div>
				<div className="min-h-0 flex-1">
					<ChannelList selection={selection} />
				</div>
				<div className="space-y-3 border-t border-gray-200 p-3 dark:border-gray-700/80">
					<LinkOverride defaultLink={defaultLink} />
					<div className="max-h-40 overflow-hidden rounded-lg bg-white py-1 ring-1 ring-black/5 dark:ring-white/10">
						<SlackPost link={defaultLink} variant="title-link" compact />
					</div>
					<div className="flex justify-between gap-2">
						<Button variant="outline" size="sm">
							Send test Slack
						</Button>
						<Button variant="default" size="sm">
							Use {selection.count} channels
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}

// Version C: Publish stays short; Slack is a second dialog right after it.
function PublishDialogC({ defaultLink }: { defaultLink: string | null }) {
	const [email, setEmail] = useState(true)
	const [shareNext, setShareNext] = useState(true)
	const selection = useChannelSelection()

	return (
		<div className="space-y-6">
			<DialogShell
				title="Publish changelog"
				description="Notify your contacts about this update."
				footer={
					<>
						<DetailsToggle />
						<DialogFooter label="Publish and notify" />
					</>
				}
			>
				<div className={ROWS_CLASS}>
					<InAppRow />
					<EmailRow email={email} onEmail={setEmail} />
					<div className="flex items-center gap-2">
						<Switch
							name="share-next"
							checked={shareNext}
							onCheckedChange={setShareNext}
							variant="branded"
							size="small"
						/>
						<span className="text-sm text-secondary">Share in Slack next</span>
						<ChangedBadge />
					</div>
				</div>
			</DialogShell>
			<div className="text-xs text-tertiary">
				After Publish, this dialog opens in its place:
			</div>
			<DialogShell
				title="Share in Slack"
				description={`Post "${POST.title}" to your customer channels.`}
				width="max-w-none"
				footer={
					<div className="mt-4 flex items-center justify-between gap-2">
						<Button variant="ghost">Skip</Button>
						<div className="flex items-center gap-2">
							<Button variant="outline">Send test Slack</Button>
							<Button variant="default" Icon={PaperPlane}>
								Send to {selection.count} channels
							</Button>
						</div>
					</div>
				}
			>
				<div className="flex gap-4 pt-2">
					<div className="flex h-[440px] w-[280px] flex-none flex-col overflow-hidden rounded-lg bg-white ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
						<ChannelList selection={selection} />
					</div>
					<div className="min-w-0 flex-1 space-y-3">
						<LinkOverride defaultLink={defaultLink} />
						<div className="flex items-center gap-2 text-xs font-medium text-tertiary">
							Preview <NewBadge />
						</div>
						<div className="overflow-hidden rounded-lg bg-white py-1 ring-1 ring-black/5 dark:ring-white/10">
							<SlackPost link={defaultLink} variant="two-buttons" compact />
						</div>
					</div>
				</div>
			</DialogShell>
		</div>
	)
}

type SendState = "sending" | "done" | "retrying"

const TOTAL = 812

function sendNumbers(state: SendState) {
	const sent = state === "sending" ? 340 : state === "retrying" ? 808 : 806
	const failures =
		state === "retrying"
			? FAILED.slice(0, 2)
			: state === "sending"
				? FAILED.slice(0, 1)
				: FAILED
	return { sent, failures, failed: failures.length }
}

function SendSummary({
	state,
	onRetry,
}: {
	state: SendState
	onRetry: () => void
}) {
	const { sent, failures, failed } = sendNumbers(state)
	return (
		<>
			<div className="space-y-2">
				<div className="text-sm font-medium text-primary">
					{state === "sending"
						? `Sending to ${TOTAL} channels`
						: `Sent to ${sent} of ${TOTAL} channels`}
				</div>
				<ProgressBar
					percentage={((sent + failed) / TOTAL) * 100}
					fillClassName="bg-blue-500"
				/>
				<div className="text-xs text-tertiary">
					{state === "sending"
						? `${TOTAL - sent - failed} left, about ${Math.ceil((TOTAL - sent - failed) / 5 / 60)} min`
						: `${failed} failed`}
				</div>
			</div>
			{failures.length > 0 && (
				<div className="space-y-1.5 border-t border-gray-200 pt-3 dark:border-gray-700/80">
					{failures.map((f) => (
						<div
							key={f.name}
							className="flex items-center justify-between gap-3 text-sm"
						>
							<span className="truncate text-primary">#{f.name}</span>
							<span className="flex-none text-xs text-tertiary">
								{f.reason}
							</span>
						</div>
					))}
				</div>
			)}
			{state !== "sending" && (
				<div className="flex justify-end">
					<Button variant="outline" size="sm" onClick={onRetry}>
						Retry failed
					</Button>
				</div>
			)}
		</>
	)
}

function badgeLabel(state: SendState) {
	const { sent } = sendNumbers(state)
	return state === "sending"
		? `Slack ${sent} / ${TOTAL}`
		: state === "done"
			? `Slack ${sent} sent`
			: "Slack retrying"
}

function EditorHeaderBar({ status }: { status?: ReactNode }) {
	return (
		<div className="flex h-12 max-h-12 min-h-12 items-center justify-between border-b border-gray-200 bg-white py-0 pl-2 pr-3 sm:pl-[17.5px] dark:border-gray-700/80 dark:bg-gray-900">
			<nav
				aria-label="Breadcrumb"
				className="flex min-w-0 max-w-full cursor-default items-center gap-2 text-sm font-medium"
			>
				<button
					type="button"
					className="-ml-1 -mr-1 min-w-0 truncate rounded px-1 py-0.5 text-secondary hover:bg-gray-100 dark:hover:bg-gray-800"
				>
					Changelog
				</button>
				<BreadcrumbSlash />
				<span className="min-w-0 truncate text-primary">{POST.title}</span>
			</nav>
			<div className="ml-auto flex shrink-0 items-center justify-center gap-2">
				{status}
				<Button
					variant="outline"
					className="justify-start text-left font-normal"
					Icon={CalendarIcon}
				>
					October 8th, 2026
				</Button>
				<Button variant="ghost" size="sm" Icon={Globe}>
					All portals
				</Button>
				<div className="ml-1 flex items-center gap-1">
					<Tooltip label="Open changelog">
						<IconButton Icon={ExternalLink} variant="ghost" />
					</Tooltip>
					<IconButton variant="ghost" aria-label="Toggle AI Assistant">
						<AiAssistantIcon className="h-3.5 w-3.5" />
					</IconButton>
					<IconButton Icon={MoreVertical} variant="ghost" />
				</div>
			</div>
		</div>
	)
}

function EditorBody() {
	return (
		<div className="h-24 bg-white px-6 pt-6 text-2xl font-semibold text-primary dark:bg-gray-900">
			{POST.title}
		</div>
	)
}

// Version A: a badge in the editor header; its popover lists the failed channels.
function ProgressA({
	state,
	onRetry,
}: {
	state: SendState
	onRetry: () => void
}) {
	return (
		<div className="overflow-hidden rounded-lg ring-1 ring-black/5 dark:ring-white/10">
			<EditorHeaderBar
				status={
					<span className="flex items-center gap-1.5">
						<NewBadge />
						<Popover
							key={state}
							align="end"
							defaultOpen
							className="w-96 space-y-3 p-4"
							content={<SendSummary state={state} onRetry={onRetry} />}
						>
							<button type="button">
								<Badge variant={state === "done" ? "green" : "default"}>
									{badgeLabel(state)}
								</Badge>
							</button>
						</Popover>
					</span>
				}
			/>
			<EditorBody />
			<div className="h-72 bg-white dark:bg-gray-900" />
		</div>
	)
}

// Version B: a full-width strip under the editor header, always visible until dismissed.
function ProgressB({
	state,
	onRetry,
}: {
	state: SendState
	onRetry: () => void
}) {
	const { sent, failures, failed } = sendNumbers(state)
	const [open, setOpen] = useState(true)
	return (
		<div className="overflow-hidden rounded-lg ring-1 ring-black/5 dark:ring-white/10">
			<EditorHeaderBar />
			<div className="space-y-3 border-b border-gray-200 bg-gray-50 px-5 py-3 dark:border-gray-700/80 dark:bg-gray-850">
				<div className="flex items-center gap-3">
					<span className="flex items-center gap-2 text-sm font-medium text-primary">
						<SlackIcon className="w-4 flex-none" />
						{state === "sending"
							? `Sending to ${TOTAL} channels`
							: `Sent to ${sent} of ${TOTAL} channels`}
					</span>
					<NewBadge />
					<div className="min-w-0 flex-1">
						<ProgressBar
							percentage={((sent + failed) / TOTAL) * 100}
							fillClassName="bg-blue-500"
						/>
					</div>
					{failed > 0 && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setOpen((v) => !v)}
						>
							{failed} failed
						</Button>
					)}
					{state !== "sending" && (
						<Button variant="outline" size="sm" onClick={onRetry}>
							Retry failed
						</Button>
					)}
				</div>
				{open && failures.length > 0 && (
					<div className="grid grid-cols-2 gap-x-6 gap-y-1 pl-6">
						{failures.map((f) => (
							<div
								key={f.name}
								className="flex items-center justify-between gap-3 text-sm"
							>
								<span className="truncate text-primary">#{f.name}</span>
								<span className="flex-none text-xs text-tertiary">
									{f.reason}
								</span>
							</div>
						))}
					</div>
				)}
			</div>
			<EditorBody />
			<div className="h-48 bg-white dark:bg-gray-900" />
		</div>
	)
}

const OTHER_POSTS = [
	{ title: "Triage rules for shared channels", date: "Sep 30, 2026" },
	{ title: "Faster search in the inbox", date: "Sep 22, 2026" },
	{ title: "SAML for every plan", date: "Sep 15, 2026" },
]

// Version C: a toast while it sends, then the status lives on the post's row in the changelog list.
function ProgressC({
	state,
	onRetry,
}: {
	state: SendState
	onRetry: () => void
}) {
	const { sent, failed } = sendNumbers(state)
	return (
		<div className="flex items-end gap-6">
			<div className="w-72 flex-none space-y-0.5 rounded-lg bg-white p-2 pb-3 ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
				<div className="px-2 pb-2 pt-1 text-xs font-medium text-tertiary">
					Published
				</div>
				<div className="flex w-full items-start gap-2 rounded-md bg-gray-100 px-2 py-1.5 dark:bg-gray-800/50">
					<span className="flex h-5 w-1.5 flex-none items-center">
						<span className="h-1.5 w-1.5 flex-none rounded-full border border-gray-400 dark:border-gray-500" />
					</span>
					<span className="flex min-w-0 flex-1 flex-col gap-1">
						<span className="truncate text-sm font-medium tracking-tight text-primary opacity-90">
							{POST.title}
						</span>
						<span className="flex items-center gap-1.5 text-xs text-tertiary">
							{POST.dateLabel}
							<Popover
								key={state}
								align="start"
								className="w-96 space-y-3 p-4"
								content={<SendSummary state={state} onRetry={onRetry} />}
							>
								<button type="button">
									<Badge
										size="sm"
										variant={
											state === "sending"
												? "default"
												: failed > 0
													? "red"
													: "green"
										}
									>
										{state === "sending"
											? `Slack ${sent} / ${TOTAL}`
											: failed > 0
												? `${failed} failed in Slack`
												: "Sent in Slack"}
									</Badge>
								</button>
							</Popover>
							<NewBadge />
						</span>
					</span>
				</div>
				{OTHER_POSTS.map((p) => (
					<div
						key={p.title}
						className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-800/50"
					>
						<span className="flex h-5 w-1.5 flex-none items-center">
							<span className="h-1.5 w-1.5 flex-none rounded-full border border-gray-400 dark:border-gray-500" />
						</span>
						<span className="flex min-w-0 flex-1 flex-col gap-0.5">
							<span className="truncate text-sm font-medium tracking-tight text-primary opacity-90">
								{p.title}
							</span>
							<span className="truncate text-xs text-tertiary">{p.date}</span>
						</span>
					</div>
				))}
			</div>
			<div className="relative min-w-[330px] max-w-[330px] overflow-hidden rounded-xl bg-white px-2 py-3 shadow-lg shadow-black/[0.1] ring-1 ring-black/[0.07] dark:bg-gray-850/90 dark:ring-white/10">
				<div className="mr-2 grid grid-cols-[auto,1fr] gap-x-2">
					<IconWithHalo
						variant={state === "done" && failed > 0 ? "warning" : "primary"}
						icon={<SlackIcon className="h-4 w-4" />}
					/>
					<div className="min-w-0">
						<div className="mb-1 mt-0.5 text-sm font-semibold">
							{state === "sending"
								? `Sending to ${TOTAL} Slack channels`
								: `Sent to ${sent} of ${TOTAL} channels`}
						</div>
						<div className="text-sm text-secondary">
							{state === "sending"
								? "You can close this. Status stays on the post."
								: `${failed} failed. Open the post to see which.`}
						</div>
						{state !== "sending" && (
							<Button
								variant="ghost"
								size="sm"
								onClick={onRetry}
								className="-ml-1 text-secondary underline hover:bg-transparent hover:text-primary"
							>
								Retry failed
							</Button>
						)}
					</div>
				</div>
				{state === "sending" && (
					<div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700">
						<div
							className="h-full bg-blue-500"
							style={{ width: `${((sent + failed) / TOTAL) * 100}%` }}
						/>
					</div>
				)}
			</div>
		</div>
	)
}

function SlackChannel({
	link,
	variant,
}: {
	link: string | null
	variant: PostVariant
}) {
	return (
		<div className="overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-black/5 dark:ring-white/10">
			<div className="flex h-12 items-center gap-2 border-b border-gray-200 px-5 font-[Lato,ui-sans-serif,system-ui]">
				<span className="text-[15px] font-bold text-[#1d1c1d]">
					# ent-openai
				</span>
				<Badge size="sm" variant="light-gray">
					Slack Connect
				</Badge>
			</div>
			<div className="pb-4 pt-2">
				<SlackPost link={link} variant={variant} />
			</div>
		</div>
	)
}

const VERSIONS = ["A", "B", "C"] as const

function ScreenRow({
	label,
	note,
	lines,
	frame = "panel",
	children,
}: {
	label: string
	note?: string
	lines: readonly [string, string, string]
	frame?: "panel" | "settings"
	children: readonly [ReactNode, ReactNode, ReactNode]
}) {
	return (
		<div className="space-y-4">
			<div className="space-y-0.5">
				<div className="text-sm font-medium text-primary">{label}</div>
				{note && <div className="text-xs text-tertiary">{note}</div>}
			</div>
			<div className="flex flex-nowrap items-start gap-10 overflow-x-auto pb-4">
				{VERSIONS.map((v, i) => (
					<div key={v} className="w-[784px] flex-none space-y-3">
						<div className="space-y-0.5">
							<div className="text-sm font-semibold text-primary">
								Version {v}
							</div>
							<div className="text-xs text-secondary">{lines[i]}</div>
						</div>
						<div className="rounded-xl bg-gray-50 px-10 py-10 ring-1 ring-black/5 dark:bg-gray-950 dark:ring-white/10">
							<div className={cn(frame === "settings" && "w-[704px]")}>
								{children[i]}
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

export function ChangelogSlackBroadcastPage() {
	const [mode, setMode] = useState("custom")
	const [pattern, setPattern] = useState(
		"https://linear.app/changelog/{date}-{slug}",
	)
	const [urlB, setUrlB] = useState("https://linear.app/changelog/{date}-{slug}")
	const [modeC, setModeC] = useState<"productlane" | "custom">("custom")
	const [urlC, setUrlC] = useState("https://linear.app/changelog/{date}-{slug}")
	const [sendState, setSendState] = useState<SendState>("sending")

	const linkA = resolveLink(mode, pattern)
	const linkB = resolveUrl(urlB)
	const linkC =
		modeC === "productlane"
			? `https://${PORTAL_DISPLAY_URL}/${POST.slug}`
			: resolveUrl(urlC)
	const retry = () => setSendState("retrying")

	if (!isDevEnvironment) return <NotFound />
	return (
		<SettingsCardInsetContext.Provider value={true}>
			<div className="min-h-screen bg-white px-10 py-10 pb-24 dark:bg-gray-900">
				<div className="space-y-14">
					<div className="max-w-4xl">
						<DesignIssueHeader
							identifier="PRO-10812"
							title="Changelog Slack broadcast: link to the customer's own changelog URL and support bulk sends to hundreds of channels"
							url="https://linear.app/productlane/issue/PRO-10812"
							summary="Linear sends each changelog to about 800 customer Slack channels, and their changelog lives on their own website. This design lets you point the post's button to your own URL, pick which channels get it, preview the post, and watch it go out with a list of channels that failed and a retry. Each screen comes in three versions side by side; a column reads top to bottom as one version."
						/>
					</div>

					<ScreenRow
						label="Settings / Changelog"
						note="The page as it is today, plus the new link setting. Saved on change, used by Slack and email."
						frame="settings"
						lines={[
							"A select with three choices, and a URL field for your website.",
							"One URL field. Empty means your Productlane changelog.",
							"A row that opens its own page, with a live preview of the post.",
						]}
					>
						{[
							<ChangelogSettingsReplica
								key="a"
								renderLink={(dimmed) => (
									<LinkSettingCard
										mode={mode}
										pattern={pattern}
										onMode={setMode}
										onPattern={setPattern}
										dimmed={dimmed}
									/>
								)}
							/>,
							<ChangelogSettingsReplica
								key="b"
								renderLink={(dimmed) => (
									<LinkUrlCard url={urlB} onUrl={setUrlB} dimmed={dimmed} />
								)}
							/>,
							<div key="c" className="space-y-10">
								<ChangelogSettingsReplica
									renderLink={(dimmed) => (
										<LinkNavRow link={linkC} dimmed={dimmed} />
									)}
								/>
								<div className="text-xs text-tertiary">
									The row opens this page:
								</div>
								<LinkSubpage
									mode={modeC}
									url={urlC}
									onMode={setModeC}
									onUrl={setUrlC}
								/>
							</div>,
						]}
					</ScreenRow>

					<ScreenRow
						label="Changelog / Agent API is now public / Publish"
						note="The dialog that opens from Publish in the changelog editor."
						lines={[
							"Everything in one dialog: channel popover, link and preview inline.",
							"The dialog stays short; channels, link and preview open in a side sheet.",
							"Publish first, then a separate Share in Slack dialog with list and preview.",
						]}
					>
						{[
							<PublishDialogA key={`a-${linkA}`} defaultLink={linkA} />,
							<PublishDialogB key={`b-${linkB}`} defaultLink={linkB} />,
							<PublishDialogC key={`c-${linkC}`} defaultLink={linkC} />,
						]}
					</ScreenRow>

					<div className="space-y-3">
						<div className="flex flex-wrap items-center gap-2">
							<span className="text-xs text-tertiary">
								Show state in all three:
							</span>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setSendState("sending")}
							>
								Sending
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setSendState("done")}
							>
								Done with failures
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setSendState("retrying")}
							>
								Retrying
							</Button>
						</div>
						<ScreenRow
							label="Changelog / Agent API is now public, after Publish"
							note="How the Slack send shows its progress, the channels that failed, and the retry."
							lines={[
								"A badge in the editor header; failures sit in its popover.",
								"A strip under the header with the bar, failures and retry in view.",
								"A toast while it sends, then a status badge on the post in the list.",
							]}
						>
							{[
								<ProgressA key="a" state={sendState} onRetry={retry} />,
								<ProgressB
									key={`b-${sendState}`}
									state={sendState}
									onRetry={retry}
								/>,
								<ProgressC key="c" state={sendState} onRetry={retry} />,
							]}
						</ScreenRow>
					</div>

					<ScreenRow
						label="Slack / #ent-openai"
						note="What customers see. Each version uses the link from its own settings above."
						lines={[
							"A Read more button under the body.",
							"The title is the link, with no button.",
							"Read more plus an All updates button for the changelog home.",
						]}
					>
						{[
							<SlackChannel key="a" link={linkA} variant="button" />,
							<SlackChannel key="b" link={linkB} variant="title-link" />,
							<SlackChannel key="c" link={linkC} variant="two-buttons" />,
						]}
					</ScreenRow>
				</div>
			</div>
		</SettingsCardInsetContext.Provider>
	)
}
